// @flow
import {
  Box,
  Container,
  IconButton,
  Grid,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import chunk from 'lodash.chunk';
import { AiOutlineArrowRight, AiOutlineArrowLeft } from 'react-icons/ai';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { motion } from 'framer-motion';
import { useInView, InView } from 'react-intersection-observer';
import * as React from 'react';
import range from 'lodash.range';

import Line from '../shared/line';
import ServiceItemsDescriptions from './services/service-items-descriptions';
import ServiceItemsImages from './services/service-items-images';

const useStyles = makeStyles((theme) => ({
  textContainer: {
    paddingTop: theme.spacing(10),
    backgroundColor: theme.palette.secondary.main,
    height: theme.spacing(80),
    [theme.breakpoints.down('sm')]: {
      height: theme.spacing(60),
    },

    [theme.breakpoints.up('sm')]: {
      height: theme.spacing(85),
    },
  },
  paragraphContainer: {
    marginTop: theme.spacing(0),
    paddingTop: theme.spacing(14),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(0),
    },
  },
  mainTitle: {
    color: 'white',
  },
  serviceTitle: {
    position: 'relative',
    color: 'white',
    textTransform: 'uppercase',
    top: -10,

    [theme.breakpoints.up('sm')]: {
      height: theme.spacing(10),
    },

    [theme.breakpoints.down('md')]: {
      fontSize: '2rem',
    },
  },
  arrow: {
    '&.MuiIconButton-colorPrimary': {
      border: `1px solid ${theme.palette.primary.main}`,
    },

    '&:last-child': {
      marginLeft: theme.spacing(2),
    },
  },
  subtitle: {
    textTransform: 'uppercase',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
}));

type Props = {
  items: Array<any>,
};

const Services = ({ items = [] }: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: false });
  /* eslint-disable-next-line */
  const [refText: ref, inViewText: inView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });
  const [index, setIndex] = React.useState(0);
  const [galleryIndex, setGalleryIndex] = React.useState(0);
  const [partitions, setPartitions] = React.useState(
    chunk(items[index].servicios, 3),
  );
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  const variants = {
    visible: { opacity: 1 },
    hidden: {
      opacity: 0,
    },
    yellow: {
      backgroundColor: theme.palette.secondary.main,
    },
    gray: {
      backgroundColor: theme.motorX.lightGray,
    },
  };

  const next = () => setGalleryIndex(galleryIndex + 1);

  const prev = () => setGalleryIndex(galleryIndex - 1);

  React.useEffect(() => {
    if (matches) {
      setPartitions(chunk(items[index].servicios, 1));
    }
  }, [matches, index]);

  React.useEffect(() => {
    setGalleryIndex(0);
  }, [partitions]);

  return (
    <motion.div
      animate={inView ? 'visible' : 'hidden'}
      ref={ref}
      variants={variants}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div className={classes.textContainer}>
        <Container maxWidth="xl">
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <InView threshold={0.1}>
                {({ inView, ref }) => (
                  <>
                    <Box
                      display="flex"
                      justifyContent="center"
                      component="div"
                      alignItems="center"
                      alignContent="center"
                      mb={5}
                    >
                      <div>
                        {range(items.length).map((number, idx) => (
                          <Line
                            key={number}
                            active={idx === index}
                            onClick={() => setIndex(idx)}
                          />
                        ))}
                      </div>
                    </Box>

                    <Box
                      animate={inView ? 'visible' : 'hidden'}
                      ref={ref}
                      variants={variants}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                      component={motion.div}
                      pl={8}
                    >
                      <Typography className={classes.mainTitle} variant="h2">
                        Servicios&reg;
                      </Typography>
                      <Typography
                        component="span"
                        className={classes.serviceTitle}
                        variant="h1"
                      >
                        {items[index].nombre}&reg;
                      </Typography>
                    </Box>
                  </>
                )}
              </InView>
            </Grid>

            <InView threshold={0.1} triggerOnce={false}>
              {({ inView, ref }) => (
                <Grid
                  container
                  item
                  xs={12}
                  spacing={4}
                  display="flex"
                  justifyContent="center"
                  component={motion.div}
                  ref={ref}
                  animate={inView ? 'visible' : 'hidden'}
                  variants={variants}
                  transition={{ duration: 1, ease: 'easeOut' }}
                >
                  {items.map((item, idx) => (
                    <React.Fragment key={item.id}>
                      {index === idx && (
                        <ServiceItemsImages
                          items={item.servicios}
                          title={item.nombre}
                          galleryIndex={galleryIndex}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Grid>
              )}
            </InView>
          </Grid>
        </Container>
      </div>

      <InView threshold={0.2}>
        {({ ref, inView }) => (
          <motion.div
            className={classes.paragraphContainer}
            ref={ref}
            animate={inView ? 'gray' : 'yellow'}
            variants={variants}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <Container maxWidth="xl">
              <motion.div
                animate={inViewText ? 'visible' : 'hidden'}
                ref={refText}
                variants={variants}
                transition={{ duration: 1, ease: 'easeOut' }}
              >
                {items.map((item, idx) => (
                  <React.Fragment key={item.id}>
                    {index === idx && (
                      <ServiceItemsDescriptions
                        items={item.servicios}
                        galleryIndex={galleryIndex}
                      />
                    )}
                  </React.Fragment>
                ))}
              </motion.div>
              <Box display="flex" pt={0} pb={5} justifyContent="center">
                {galleryIndex !== 0 && (
                  <IconButton
                    className={classes.arrow}
                    color="primary"
                    component="span"
                    onClick={prev}
                  >
                    <AiOutlineArrowLeft />
                  </IconButton>
                )}

                {galleryIndex !== partitions.length - 1 && (
                  <IconButton
                    className={classes.arrow}
                    color="primary"
                    component="span"
                    onClick={next}
                  >
                    <AiOutlineArrowRight />
                  </IconButton>
                )}
              </Box>
            </Container>
          </motion.div>
        )}
      </InView>
    </motion.div>
  );
};

export default Services;
